import React, { useState, useEffect } from 'react';
import './DiffParserLoader.css';

const funnyMessages = [
  "🎨 Teaching pixels to dance...",
  "🔮 Consulting the code oracle...",
  "🧙‍♂️ Casting syntax spells...",
  "🎭 Rehearsing the diff performance...",
  "🎪 Juggling semicolons...",
  "🎯 Aligning the stars and braces...",
  "🎲 Rolling for critical success...",
  "🎸 Tuning the code symphony...",
  "🎨 Painting with binary brushes...",
  "🔬 Mixing potions of logic...",
  "🎩 Pulling code from the hat...",
  "🌈 Adding some rainbow brackets...",
  "🎯 Aiming for perfection...",
  "🎪 Training the code monkeys...",
  "🎭 Writing the diff screenplay...",
  "🎨 Sketching elegant solutions...",
  "🔮 Predicting merge conflicts...",
  "🎸 Rocking the parser...",
  "🎲 Fortune favors the bold coder...",
  "🌟 Sprinkling magic dust on your code...",
  "🎯 Hitting the bullseye...",
  "🎪 Performing code acrobatics...",
  "🧙‍♂️ Brewing a diff potion...",
  "🎭 Setting the stage for success...",
  "🎨 Crafting digital masterpieces...",
  "🔬 Analyzing molecular code structures...",
  "🎩 Abracadabra, code will appear...",
  "🌈 Following the diff rainbow...",
  "🎯 Calibrating precision instruments...",
  "🎸 Composing a code symphony..."
];

const DiffParserLoader = ({ isVisible, onCancel, fileName = 'file', progressInfo = null }) => {
  const [message, setMessage] = useState(funnyMessages[0]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [modifiedFiles, setModifiedFiles] = useState([]);

  useEffect(() => {
    if (!isVisible) {
      setIsCompleted(false);
      setIsClosing(false);
      return;
    }

    // Rotate messages every 2 seconds
    const interval = setInterval(() => {
      const randomMessage = funnyMessages[Math.floor(Math.random() * funnyMessages.length)];
      setMessage(randomMessage);
    }, 2000);

    return () => clearInterval(interval);
  }, [isVisible]);

  useEffect(() => {
    // Listen for completion event
    const handleComplete = (event) => {
      setIsCompleted(true);
      // Extract modified files info from event
      if (event.detail && event.detail.files) {
        setModifiedFiles(event.detail.files);
      } else if (event.detail && event.detail.filePath) {
        // Single file case
        setModifiedFiles([{
          path: event.detail.filePath,
          operation: event.detail.operation || 'modified'
        }]);
      }
      // Don't auto-close if there are modified files to show
      const hasFiles = event.detail?.files?.length > 0 || event.detail?.filePath;
      if (!hasFiles) {
        // Auto-close after 2 seconds if no files to display
        setTimeout(() => {
          setIsClosing(true);
          setTimeout(() => {
            if (onCancel) onCancel();
          }, 500); // Wait for fade-out animation
        }, 2000);
      }
    };

    window.addEventListener('diffParserComplete', handleComplete);
    return () => window.removeEventListener('diffParserComplete', handleComplete);
  }, [onCancel]);

  if (!isVisible) return null;

  const handleCancelClick = () => {
    setIsClosing(true);
    setTimeout(() => {
      if (onCancel) onCancel();
    }, 300);
  };

  return (
    <div className={`diff-parser-overlay ${isClosing ? 'closing' : ''}`}>
      <div className={`diff-parser-modal ${isCompleted ? 'completed' : ''} ${isClosing ? 'closing' : ''}`}>
        {!isCompleted ? (
          <>
            <div className="diff-parser-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <h3 className="diff-parser-title">
              Applying Diff Changes
            </h3>
            <div className="diff-parser-details">
              <div className="diff-detail-item">
                <span className="detail-label">📄 File:</span>
                <span className="detail-value">{fileName}</span>
              </div>
              {progressInfo?.stage && (
                <div className="diff-detail-item">
                  <span className="detail-label">⚙️ Stage:</span>
                  <span className="detail-value">{progressInfo.stage}</span>
                </div>
              )}
              {progressInfo?.confidence && (
                <div className="diff-detail-item">
                  <span className="detail-label">📊 Confidence:</span>
                  <span className="detail-value">{progressInfo.confidence}%</span>
                </div>
              )}
              {progressInfo?.llmActive && (
                <div className="diff-detail-item">
                  <span className="detail-label">🤖 LLM:</span>
                  <span className="detail-value">Processing...</span>
                </div>
              )}
              {progressInfo?.operation && (
                <div className="diff-detail-item">
                  <span className="detail-label">🔧 Operation:</span>
                  <span className="detail-value">{progressInfo.operation}</span>
                </div>
              )}
            </div>
            <p className="diff-parser-message">{message}</p>
            <button className="diff-parser-cancel-btn" onClick={handleCancelClick} type="button">
              Cancel
            </button>
          </>
        ) : (
          <>
            <div className="diff-parser-success">
              <div className="success-icon">✅</div>
              <h3 className="success-title">Diff Applied Successfully!</h3>
              <div className="diff-parser-details">
                <div className="diff-detail-item">
                  <span className="detail-label">📄 File:</span>
                  <span className="detail-value">{fileName}</span>
                </div>
                {progressInfo?.stage && (
                  <div className="diff-detail-item">
                    <span className="detail-label">✓ Completed:</span>
                    <span className="detail-value">{progressInfo.stage}</span>
                  </div>
                )}
                {progressInfo?.operation && (
                  <div className="diff-detail-item">
                    <span className="detail-label">🔧 Last Operation:</span>
                    <span className="detail-value">{progressInfo.operation}</span>
                  </div>
                )}
              </div>
              {modifiedFiles.length > 0 && (
                <div className="modified-files-section">
                  <h4 className="modified-files-title">📝 Modified Files ({modifiedFiles.length}):</h4>
                  <div className="modified-files-list">
                    {modifiedFiles.map((file, index) => {
                      const operationIcon = {
                        'create': '🆕',
                        'modified': '✏️',
                        'delete': '🗑️',
                        'update': '📝'
                      }[file.operation] || '📄';
                      
                      return (
                        <button
                          key={index}
                          className="modified-file-item"
                          onClick={() => {
                            // Dispatch event to open the file
                            window.dispatchEvent(new CustomEvent('openFile', {
                              detail: {
                                filePath: file.path,
                                projectId: file.projectId,
                                serviceName: file.serviceName
                              }
                            }));
                            // Close the modal
                            handleCancelClick();
                          }}
                          type="button"
                          title={`Click to review ${file.path}`}
                        >
                          <span className="file-operation-icon">{operationIcon}</span>
                          <span className="file-path">{file.path}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <button className="diff-parser-close-btn" onClick={handleCancelClick} type="button">
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DiffParserLoader;
